import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
} from "@mui/material";
import { useTenantsQuery } from "Tenants/Api/Queries/useTenantsQuery";
import { useSignUpMutation } from "Users/Api/Mutations/useSignUpMutation";
import { type FC, useEffect, useState } from "react";

type Props = {
	open: boolean;
	onClose: () => void;
};

export const CreateUserDialog: FC<Props> = ({ open, onClose }) => {
	const [login, setLogin] = useState("");
	const [password, setPassword] = useState("");
	const [tenantCode, setTenantCode] = useState("");
	const [error, setError] = useState("");
	const signUpMutation = useSignUpMutation();
	const { data: tenantsData } = useTenantsQuery();

	const tenants = tenantsData?.items ?? [];

	useEffect(() => {
		if (!open) {
			setLogin("");
			setPassword("");
			setTenantCode("");
			setError("");
		}
	}, [open]);

	const handleClose = () => {
		setLogin("");
		setPassword("");
		setTenantCode("");
		setError("");
		onClose();
	};

	const handleSubmit = async () => {
		if (!login.trim()) {
			setError("Login is required");
			return;
		}

		if (!password.trim()) {
			setError("Password is required");
			return;
		}

		if (!tenantCode.trim()) {
			setError("Tenant is required");
			return;
		}

		try {
			const result = await signUpMutation.mutateAsync({
				login: login.trim(),
				password: password.trim(),
				tenantCode: tenantCode.trim(),
			});

			// Success case
			if (result.status === 201) {
				handleClose();
				return;
			}

			// Handle error cases
			if (
				(result.status === 409 ||
					result.status === 400 ||
					result.status === 403) &&
				result.data?.errors
			) {
				// Extract error message from the errors object
				const errorMessages = Object.values(result.data.errors)
					.flat()
					.map((err: any) => err.message)
					.filter(Boolean);

				if (errorMessages.length > 0) {
					setError(errorMessages[0]);
					return;
				}
			}

			// If we got here, something unexpected happened
			setError("Failed to create user. Please try again.");
		} catch (err) {
			setError("Failed to create user. Please try again.");
		}
	};

	const isLoading = signUpMutation.isPending;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Create New User</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					margin="dense"
					label="Login"
					type="text"
					fullWidth
					variant="outlined"
					value={login}
					onChange={(e) => {
						setLogin(e.target.value);
						setError("");
					}}
					error={!!error}
					placeholder="Enter user login"
					sx={{ mb: 2 }}
				/>
				<TextField
					margin="dense"
					label="Password"
					type="password"
					fullWidth
					variant="outlined"
					value={password}
					onChange={(e) => {
						setPassword(e.target.value);
						setError("");
					}}
					error={!!error}
					placeholder="Enter password"
					sx={{ mb: 2 }}
				/>
				<TextField
					select
					margin="dense"
					label="Tenant"
					fullWidth
					variant="outlined"
					value={tenantCode}
					onChange={(e) => {
						setTenantCode(e.target.value);
						setError("");
					}}
					error={!!error}
					helperText={error}
					placeholder="Select tenant"
				>
					{tenants.map((tenant) => (
						<MenuItem key={tenant.tenantID} value={tenant.code}>
							{tenant.code}
						</MenuItem>
					))}
				</TextField>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="inherit">
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					color="primary"
					disabled={isLoading}
				>
					{isLoading ? "Creating..." : "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
